import React from 'react';

import { Badge, Button, Group, Modal, Paper, Stack, Text, useMantineTheme } from '@mantine/core';
import dayjs from 'dayjs';
import useDeleteAthleteAppointment from 'hooks/athleteAppointment/useDeleteAthleteAppointment';
import useDeleteAthleteClass from 'hooks/athleteClass/useDeleteAthleteClass';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import { TOTAL_UNCREDITED_RESERVATIONS_QUERY_KEY } from 'hooks/reports/reservations/uncredited-reservations/useTotalUncreditedReservations';
import { UNCREDITED_RESERVATIONS_QUERY_KEY } from 'hooks/reports/reservations/uncredited-reservations/useUncreditedReservations';
import { useQueryClient } from 'react-query';
import { UncreditedEvent } from 'types';
import notificationHandler from 'utils/notifications';

interface CancelReservationModalProps {
  opened: boolean;
  onClose: () => void;
  reservation: UncreditedEvent | null;
}

const CancelReservationModal = ({
  opened,
  onClose,
  reservation,
}: CancelReservationModalProps): JSX.Element => {
  const theme = useMantineTheme();
  const { successNotification, errorNotification } = notificationHandler();
  const athleteTracking = useAthleteTracking();
  const queryClient = useQueryClient();
  const deleteAthleteAppointment = useDeleteAthleteAppointment();
  const deleteAthleteClassMutation = useDeleteAthleteClass();

  const handleConfirmDelete = (): void => {
    if (!reservation) return;

    const isClass = reservation.type === 'Class';
    const isAppointment = reservation.type === 'Appointment';

    if (isClass) {
      // Delete class reservation
      deleteAthleteClassMutation.mutate(reservation.athleteEventId, {
        onSuccess: () => {
          successNotification('Athlete reservation cancel success', 'Reservation successfully cancelled.');
          athleteTracking.trackClassCancelled(
            reservation.eventId,
            reservation.eventTypeName,
            false, // consideredLateCancel - not available in UncreditedEvent
            false, // creditDeduction - not available in UncreditedEvent
            0, // lateFee - not available in UncreditedEvent
          );
          queryClient.invalidateQueries([UNCREDITED_RESERVATIONS_QUERY_KEY]);
          queryClient.invalidateQueries([TOTAL_UNCREDITED_RESERVATIONS_QUERY_KEY]);
          onClose();
        },
        onError: error => {
          errorNotification('Athlete reservation cancel failed', error.response?.data.message);
        },
      });
    }

    if (isAppointment) {
      // Delete appointment reservation
      deleteAthleteAppointment.mutate(reservation.athleteEventId, {
        onSuccess: () => {
          successNotification('Athlete reservation cancel success', 'Reservation successfully canceled.');
          athleteTracking.trackAppointmentCancelled(
            reservation.eventId,
            reservation.eventTypeName,
            false, // consideredLateCancel - not available in UncreditedEvent
            false, // creditDeduction - not available in UncreditedEvent
            0, // lateFee - not available in UncreditedEvent
          );
          queryClient.invalidateQueries([UNCREDITED_RESERVATIONS_QUERY_KEY]);
          queryClient.invalidateQueries([TOTAL_UNCREDITED_RESERVATIONS_QUERY_KEY]);
          onClose();
        },
        onError: error => {
          errorNotification('Athlete reservation cancel failed', error.response?.data.message);
        },
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw='bold'>Cancel Reservation</Text>}
      centered
      size='md'
    >
      <Stack spacing='md'>
        <Text>Are you sure you want to remove this reservation from the athlete?</Text>

        <Paper p='sm' bg='#F5E7E7' style={{ borderColor: '#A34545' }} withBorder>
          <Text c='#A34545' size='sm'>
            The athlete will lose their spot in this {reservation?.type?.toLowerCase() || 'event'}.
          </Text>
        </Paper>

        {reservation && (
          <Paper p='sm' withBorder>
            <Stack spacing='xs'>
              <Group position='apart'>
                <Text size='sm' fw='bold' c='dimmed'>
                  Event:
                </Text>
                <Badge
                  bg={reservation.eventTypeColor ? reservation.eventTypeColor : theme.colors.gray[0]}
                  c={theme.white}
                >
                  {reservation.eventTypeName}
                </Badge>
              </Group>
              <Group position='apart'>
                <Text size='sm' fw='bold' c='dimmed'>
                  Date & Time:
                </Text>
                <Text size='sm'>
                  {dayjs(reservation.startDate).format('MMM D, YYYY')} {reservation.startTime}
                </Text>
              </Group>
            </Stack>
          </Paper>
        )}

        <Group position='right' spacing='sm' mt='md'>
          <Button variant='outline' onClick={onClose} color='dark'>
            Cancel
          </Button>
          <Button
            color='red'
            onClick={handleConfirmDelete}
            loading={deleteAthleteAppointment.isLoading || deleteAthleteClassMutation.isLoading}
            data-cy='confirm-delete-reservation'
          >
            Remove Reservation
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CancelReservationModal;
